/**
 * Single-key hash routing for sidebar pages whose URL hash is just the active page key
 * (e.g. #recipe-builder). Wraps the hashNav primitives so class pages can deep-link, react to
 * back/forward, and write the hash on navigation without each re-implementing the lifecycle.
 *
 * Pages with a composite hash (section + sub-entry) or hash-driven side effects aren't served by
 * this and should use the hashNav primitives directly.
 */
import { readHash, setHash, syncHash, initHashNav } from "./hashNav";

export class HashPageRouter<T extends string> {
  private readonly pages: readonly T[];
  private readonly defaultPage: T;
  private cleanup: (() => void) | null = null;

  constructor(pages: readonly T[], defaultPage: T) {
    this.pages = pages;
    this.defaultPage = defaultPage;
  }

  /** Validated page for a raw hash; unknown hashes resolve to the default so a bad anchor can't render blank. */
  resolve(hash: string): T {
    return (this.pages as readonly string[]).includes(hash) ? (hash as T) : this.defaultPage;
  }

  /** Page to open on first render, taken from the current URL hash. */
  initialPage(): T {
    return this.resolve(readHash());
  }

  /**
   * Normalize the URL to the current page without a new history entry, then invoke `onChange` whenever
   * the hash later resolves to a different page. `getCurrent` is read live so back/forward and repeated
   * navigations compare against the latest page, not a stale snapshot. Call stop() to detach.
   */
  start(getCurrent: () => T, onChange: (page: T) => void): void {
    syncHash(getCurrent());
    this.cleanup = initHashNav((hash) => {
      const page = this.resolve(hash);
      if (page !== getCurrent()) onChange(page);
    });
  }

  stop(): void {
    this.cleanup?.();
    this.cleanup = null;
  }

  /** Write a page to the hash, pushing a history entry (user navigation). */
  navigate(page: T): void {
    setHash(page);
  }
}
